
import Vue, { PropType, VueConstructor } from "vue"
import axios, { AxiosError, AxiosResponse } from "axios"
import { EventBus } from "@/plugins/eventBus"
import ContactsAutocompleteMixin from "@/mixins/ContactsAutocompleteMixin.vue"

interface BaseContact {
  first_name: string
  last_name: string
  email: string|null
  client_id: number
}

interface Contact extends BaseContact {
  phone: string|null
  id: number|null
  contact_phone: string|null
  contact_email: string|null
}

interface ContactBody extends BaseContact {
  mobile_phone_number: string|null
}

export default (
  Vue as VueConstructor<Vue & InstanceType<typeof ContactsAutocompleteMixin>>).extend({
  name: "Contact",
  mixins: [
    ContactsAutocompleteMixin
  ],
  components: {
  },
  props: {
    orderContact: {
      type: Object as PropType<Contact>,
      required: true
    },
    position: {
      type: Number,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    initialContact: {id:null, first_name: '', last_name: '', email: null, phone: null} as Contact,
    formIsValid: false,
    method: 'post' as 'post'|'put',
    modalTitle: '',
    dialog: false,
    emailRegex: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[\d]{1,3}\.[\d]{1,3}\.[\d]{1,3}\.[\d]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    disableInput: false,
    showDeleteContact: false,
    inputContact: {id:null, first_name: '', last_name: '', email: null, phone: null} as Contact,
    refContact : {id:null, first_name: '', last_name: '', email: null, phone: null} as Contact,
    svgInputContact: {id:null, first_name: '', last_name: '', email: null, phone: null} as Contact,
  }),
  computed: {
    emailRule() {
      return (v: string) => (this.emailRegex.test(v) || !v) || this.$vuetify.lang.t('$vuetify.invalid_email')
    },
    phoneNumberRule() {
      return (v: string) => (!v || (v.length === 10)) || this.$vuetify.lang.t('$vuetify.invalid_mobile_phone_number')
    },
    noNumberRule() {
      return (v: string) => {
        if (!v) return true
        return !/\d/.test(v) || this.$vuetify.lang.t('$vuetify.no_numbers_allowed')
      }
    },
    hasRequiredDataContact() {
      return !!(this.inputContact && (this.inputContact.email || this.inputContact.phone))
    },
    getContactDisplayName() {
      return (contact: Contact | null) => {
        if (!contact) return ''
        if (!contact.first_name && !contact.last_name) {
          return this.hintContactBuilder(contact)
        }
        if (!contact.first_name && contact.last_name) return contact.last_name.toUpperCase()
        if (contact.first_name && !contact.last_name) return contact.first_name
        return `${contact.first_name} ${contact.last_name.toUpperCase()}`
      }
    }
  },
  watch: {
    orderContact: {
      handler(newVal: Contact) {
        if (newVal && newVal.id) {
          const existingContact = this.contacts.find(contact => contact.id === newVal.id);
          if (existingContact) {
            this.inputContact = existingContact as Contact;
          } else {
            this.inputContact = newVal;
            this.getContacts();
          }
        } else if (newVal && (newVal.contact_phone !== null || newVal.contact_email !== null || newVal.first_name || newVal.last_name)) {
          this.inputContact = newVal;
        } else if (newVal) {
          this.inputContact = newVal;
        }
      },
      deep: true,
      immediate: true
    },
    inputContact() {
      this.$emit('contactSelected', this.inputContact)
      this.showDeleteContact = this.showingDeleteContact()
    },
    contacts() {
      if (this.inputContact && this.inputContact.id && this.contacts.length > 0) {
        const existingContact = this.contacts.find(contact => contact.id === this.inputContact.id);
        if (existingContact) {
          this.inputContact = existingContact as Contact;
        }
      }
    },
    disabled() {
      this.showDeleteContact = this.showingDeleteContact()
    },
    position: {
      handler(newVal: number|null) {
        if (newVal === null) {
          this.inputContact = this.refContact
          this.$emit('contactSelected', this.inputContact)
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.inputContact = this.$props.orderContact;
    this.svgInputContact = { ...this.inputContact }
    this.initialContact = { ...this.inputContact }
    this.showDeleteContact = this.showingDeleteContact()
  },
  methods: {
    /**
     * Build the hint for the contact
     * @param item Contact
     * @returns string
     */
    hintContactBuilder(item: Contact) {
      let hint = '';
      if (item.email) {
        hint += item.email
      }
      if (item.phone) {
        hint += `${item.email ? ' - ' : ''}${item.phone}`
      }
      return hint
    },
    /**
     * Close the modal
     */
    closeModal() {
      this.inputContact = { ...this.svgInputContact }
      this.getContacts()
      this.dialog = false
    },
    /**
     * Check if the delete contact button should be shown
     * @returns boolean
     */
    showingDeleteContact(): boolean {
      return (!this.disabled && this.inputContact !== null && (this.inputContact?.email != null || this.inputContact?.phone != null))
    },
    /**
     * Open the modal to create or edit a contact
     * @param method 'post'|'put'
     * @param title title of the modal
     */
    openModal(method: 'post'|'put', title:string) {
      this.method = method
      this.modalTitle = this.$vuetify.lang.t(`$vuetify.${title}`)
      this.dialog = true
      if (method === 'post') {
        this.inputContact = { ...this.refContact }
      }
      this.initialContact = { ...this.inputContact }
    },
    /**
     * Submit the contact form
     */
    submitContact() {
      let body: ContactBody | ContactBody[] = {
        first_name: this.inputContact.first_name,
        last_name: this.inputContact.last_name,
        email: this.inputContact.email?.length === 0 ? null : this.inputContact.email,
        client_id: this.$props.position,
        mobile_phone_number: this.inputContact.phone?.length === 0 ? null : this.inputContact.phone
      }
      let url = ''
      if (this.method === 'post') {
        url = '/v1/contacts'
        body = [body]
      } else {
        url = `/v1/contacts/${this.inputContact.id}`
      }
      axios[this.method](url, body)
        .then((response: AxiosResponse) => {
          let action = this.method === 'post' ? 'created' : 'updated'
          let message = "contact_successfully_" + action
          let color = 'success'
          if (Array.isArray(response.data) &&  typeof response.data[0] === "string") {
            message = "invalid_data"
            color = 'error'
            this.dialog = false
          }  else {
            if(this.method === 'post') {
              this.inputContact.id = response.data[0][0]
            }
            this.$emit('contactSelected', this.inputContact)
            this.dialog = false
          }
          (this.$refs.autocomplete as any).blur();
          this.getContacts()
          EventBus.$emit('snackbar',{message: this.$vuetify.lang.t(`$vuetify.${message}`), color: color})
          this.showDeleteContact = true
        })
        .catch((error: AxiosError) => {
          let message = ""
          if (error.response?.data[0][0] === "Contact already created") {
            message = "contact_already_created"
            this.inputContact = this.refContact
          }
          EventBus.$emit('snackbar',{color: 'error', message: this.$vuetify.lang.t(`$vuetify.${message}`)})        })
    }
  }
})
